import {
	isPhotoRotateSupported,
	isClearPhotoZoneSupported,
	isDeletePhotoZoneSupported,
} from "../components/bottom-photo-menus/bottom-photo-menu/menu-info";
import { isPhotoEditingPanelSupported } from "../components/bottom-photo-menus/photo-editing-panel/menu-info";
import {
	areSmartFiltersSupported,
	isPhotoFiltersPanelSupported,
} from "../components/bottom-photo-menus/photo-filters-panel/menu-info";
import { isPhotoTransformSupported } from "../components/bottom-photo-menus/photo-transform-panel/menu-info";
import { IDashboardContext, useDashboardContext } from "../contexts/dashboard-context";
import { useDefinition } from "./useDefinition";
import { isSwapModeAllowed } from "../components/slideshow-preview/hooks/use-photo-swapping";
import { useIsProductAdvancedEditMode } from "./useIsProductAdvancedEditMode";
import { useCallback } from "react";
import { useMessageToState } from "./useMessageToState";

export function updateZoneState(
	zone: eXpress.core.IZone | undefined,
	page: eXpress.core.IPageZone | undefined,
	dashboard: IDashboardContext,
	definition: eXpress.core.IDefinition | null,
	isProductWithAdvancedEditMode: boolean,
) {
	const isEditableZone = (testZone: eXpress.core.IZone | null | undefined) =>
		eXpress.core.isPhotoZone(testZone) && testZone.canChangeContent();

	const isDeletableZone = (testZone: eXpress.core.IZone | null | undefined) =>
		eXpress.core.isPhotoZone(testZone) && dashboard.canDeletePhotoZone(testZone);

	const isEditablePhotoZone = isEditableZone(zone);
	const hasEditablePhotoZones =
		isEditablePhotoZone || page?.spread.photoZones.some(isEditableZone) || false;

	// Transform
	const showTransform =
		isProductWithAdvancedEditMode || // Advanced products can add photozone's
		page?.definition?.canChangeDesign() || // Definition has no canTransform
		false; // Make boolean
	const canTransform = isPhotoTransformSupported(zone, dashboard);

	const hasOnePhotoZonePerSpread = !definition?.spreads.some(
		(spread) => spread.getFillablePhotoZones(false, "photo").length > 1,
	);

	const showSwap = !hasOnePhotoZonePerSpread || isProductWithAdvancedEditMode;
	const canSwap = isSwapModeAllowed(dashboard, zone);

	// Effects
	const showEffects =
		areSmartFiltersSupported(dashboard) && (zone ? isEditablePhotoZone : hasEditablePhotoZones);
	const canApplyEffects = isPhotoFiltersPanelSupported(zone, dashboard);

	//Rotate
	const showRotatePhoto = zone ? isEditablePhotoZone : hasEditablePhotoZones;
	const canRotatePhoto = isPhotoRotateSupported(zone, dashboard);

	// TODO: extra check for feature support or can the smart filters check be reused?
	const showPhotoEditing =
		areSmartFiltersSupported(dashboard) && (zone ? isEditablePhotoZone : hasEditablePhotoZones);
	const canApplyPhotoEditing = isPhotoEditingPanelSupported(zone);

	// Clear/Delete
	let showClearPhotoZone = false;
	let canClearPhotoZone = false;

	let canDeletePhotoZone = false;
	let showDeletePhotoZone = false;

	if (zone) {
		showDeletePhotoZone = isProductWithAdvancedEditMode ? isDeletableZone(zone) : false;
		showClearPhotoZone = zone.isEmpty
			? !showDeletePhotoZone // Don't show if empty and we can delete the zone
			: isEditablePhotoZone;

		canClearPhotoZone = isClearPhotoZoneSupported(zone, dashboard);
		canDeletePhotoZone = isDeletePhotoZoneSupported(zone, dashboard);

		showDeletePhotoZone = showClearPhotoZone ? false : showDeletePhotoZone;
	} else {
		showClearPhotoZone = hasEditablePhotoZones; // Show it there are photozones to prevent UI shift
		canClearPhotoZone = false;

		canDeletePhotoZone = false;
		showDeletePhotoZone = isProductWithAdvancedEditMode && !hasEditablePhotoZones; // Show if clear is enabled and photosones can be remove
	}

	return {
		showTransform,
		canTransform,

		showSwap,
		canSwap,

		showRotatePhoto,
		canRotatePhoto,

		showEffects,
		canApplyEffects,

		showPhotoEditing,
		canApplyPhotoEditing,

		showDeletePhotoZone,
		canDeletePhotoZone,

		showClearPhotoZone,
		canClearPhotoZone,
	};
}

export const useTopPhotoMenu = () => {
	const dashboard = useDashboardContext();
	const definition = useDefinition();
	const isProductWithAdvancedEditMode = useIsProductAdvancedEditMode();

	const getTopPhotoMenuState = useCallback(
		(editor: eXpress.core.IEditor) => {
			const zone = editor.selection.getCurrentZone();
			const page = editor.selection.getLogicalSelectedPage() || undefined;
			const photoZone = eXpress.core.isPhotoZone(zone) ? zone : undefined;

			return {
				// Hide top panel completely if no photo zone is selected
				hide: !photoZone,
				...updateZoneState(
					zone,
					page,
					dashboard,
					definition,
					isProductWithAdvancedEditMode,
				),
			};
		},
		[dashboard, definition, isProductWithAdvancedEditMode],
	);

	return useMessageToState({
		name: "useTopPhotoMenu",
		getState: getTopPhotoMenuState,
	});
};
